import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Modal, Button, Form, Badge } from 'react-bootstrap';
import { FaWhatsapp } from 'react-icons/fa';
import { toast } from 'react-toastify';
import Select from 'react-select';
import { remindPledgeApi } from '../../api/pledges';
import { groupApi } from '../../api/guestList';
import { useAuthContext } from '../../context/AuthContext';
import StyledDataTable from '../../components/StyledDataTable/StyledDataTable';
import SpinnerLoader from '../../components/Buttons/SpinnerLoader';
import { handleApiResponse, showApiMessage } from '../global/ApiMessageModal/ApiMessageModal';

const containsEmoji = (text) => {
  if (!text) return false;
  try {
    return /\p{Extended_Pictographic}/u.test(text);
  } catch (e) {
    return /[\uD800-\uDFFF]/.test(text);
  }
};

const KumbushaMichangoModal = ({ show, onHide }) => {
  const { isAuthenticated, currentEvent } = useAuthContext();

  const [messageTypes, setMessageTypes] = useState({ sms: false, whatsapp: false });
  const [reminderText, setReminderText] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGuests, setSelectedGuests] = useState([]);
  const [sendingMessage, setSendingMessage] = useState(false);

  const [guestsData, setGuestsData] = useState([]);
  const [guestsLoading, setGuestsLoading] = useState(false);
  const [credits, setCredits] = useState({ sms: 0, whatsapp: 0 });
  const [groups, setGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);

  const fetchGroups = async () => {
    try {
      const response = await groupApi.getGroups();
      const res = response?.data || response;
      let groupsData = [];
      if (res && res.data) {
        if (Array.isArray(res.data.data)) {
          groupsData = res.data.data;
        } else if (Array.isArray(res.data)) {
          groupsData = res.data;
        }
      } else if (Array.isArray(res)) {
        groupsData = res;
      }
      setGroups(groupsData);
    } catch (err) {
      console.error('Error fetching groups:', err);
      setGroups([]);
    }
  };

  const groupOptions = useMemo(() => {
    return groups.map(group => ({
      value: group.id,
      label: group.group_name
    }));
  }, [groups]);

  const fetchGuests = async () => {
    setGuestsLoading(true);
    try {
      const response = await remindPledgeApi.getReminderList();
      const payload = response?.data?.data || response?.data || response;

      const guests = Array.isArray(payload) ? payload : (payload?.pledges || []);

      if (!reminderText) {
        if (payload?.message_template) {
          setReminderText(payload.message_template);
        } else if (payload?.pledge_reminder_message) {
          setReminderText(payload.pledge_reminder_message);
        }
      }

      setCredits({
        sms: payload?.sms_credits || 0,
        whatsapp: payload?.whatsapp_credits || 0
      });

      const formattedGuests = Array.isArray(guests) ? guests.map(guest => ({
        id: guest.id,
        name: guest.full_name || guest.name,
        phone: guest.mobile || guest.phone_number || guest.phone,
        amount: parseFloat(guest.amount || guest.pledge_amount || 0),
        collected: parseFloat(guest.amount_collected || guest.amount_paid || 0),
      })) : [];

      setGuestsData(formattedGuests);
    } catch (err) {
      console.error('Error fetching guests:', err);
      setGuestsData([]);
    } finally {
      setGuestsLoading(false);
    }
  };

  useEffect(() => {
    if (show) {
      fetchGuests();
      fetchGroups();
    }
  }, [show]);

  // Map guests to their groups from the groups members list
  const guestsWithGroups = useMemo(() => {
    return guestsData.map(guest => {
      const guestGroups = groups
        .filter(group => Array.isArray(group.members) && group.members.some(member => member.id === guest.id))
        .map(group => ({ id: group.id, group_name: group.group_name }));
      return { ...guest, groups: guestGroups };
    });
  }, [guestsData, groups]);

  const filteredGuests = useMemo(() => {
    return guestsWithGroups.filter(guest => {
      const matchesSearch =
        guest.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        guest.phone?.includes(searchTerm);
      if (selectedGroups && selectedGroups.length > 0) {
        const selectedGroupIds = selectedGroups.map(g => g.value);
        const guestGroupIds = guest.groups?.map(g => g.id) || [];
        const matchesGroup = guestGroupIds.some(id => selectedGroupIds.includes(id));
        return matchesSearch && matchesGroup;
      }
      return matchesSearch;
    });
  }, [guestsWithGroups, searchTerm, selectedGroups]);

  const toggleContactSelection = (id) => {
    setSelectedGuests(prev =>
      prev.includes(id)
        ? prev.filter(guestId => guestId !== id)
        : [...prev, id]
    );
  };

  const selectAllFiltered = (e) => {
    if (e.target.checked) {
      setSelectedGuests(filteredGuests.map(g => g.id));
    } else {
      setSelectedGuests([]);
    }
  };

  const columns = [
    {
      name: (
        <Form.Check
          type="checkbox"
          checked={filteredGuests.length > 0 && selectedGuests.length === filteredGuests.length}
          onChange={selectAllFiltered}
        />
      ),
      width: '50px',
      cell: row => (
        <Form.Check
          type="checkbox"
          checked={selectedGuests.includes(row.id)}
          onChange={() => toggleContactSelection(row.id)}
        />
      )
    },
    {
      name: 'Name',
      selector: row => row.name,
      sortable: true,
      grow: 3,
      minWidth: '180px',
      cell: row => {
        const groupNames = Array.isArray(row.groups) && row.groups.length > 0
          ? row.groups.map(g => g.group_name || g).join(', ')
          : '';
        return (
          <div
            className="d-flex align-items-center gap-2 py-2 w-100"
            style={{ cursor: 'pointer' }}
            onClick={(e) => {
              e.stopPropagation();
              toggleContactSelection(row.id);
            }}
          >
            <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 d-none d-sm-flex" style={{ width: '28px', height: '28px' }}>
              <i className="bi bi-person-fill" style={{ fontSize: '0.85rem' }}></i>
            </div>
            <div>
              <span className="text-dark d-block fw-medium" style={{ fontSize: '0.85rem' }}>{row.name}</span>
              {groupNames && <small className="text-muted d-block" style={{ fontSize: '0.75rem' }}>Group: {groupNames}</small>}
            </div>
          </div>
        );
      }
    },
    {
      name: 'Phone',
      selector: row => row.phone,
      sortable: true,
      grow: 1.5,
      minWidth: '130px',
      cell: row => <span className="text-dark text-nowrap" style={{ fontSize: '0.85rem' }}>{row.phone || 'N/A'}</span>
    },
    {
      name: 'Ahadi',
      selector: row => row.amount,
      sortable: true,
      grow: 1.5,
      minWidth: '125px',
      cell: row => <span className="text-dark fw-bold text-nowrap" style={{ fontSize: '0.82rem' }}>TZS {(row.amount || 0).toLocaleString()}</span>
    },
    {
      name: 'Mchango',
      selector: row => row.collected,
      sortable: true,
      grow: 2.5,
      minWidth: '160px',
      cell: row => (
        <div style={{ paddingRight: '20px' }}>
          <span className="text-success fw-bold text-nowrap" style={{ fontSize: '0.82rem' }}>
            TZS {(row.collected || 0).toLocaleString()}
          </span>
        </div>
      )
    }
  ];

  const handleSendReminder = async () => {
    if (!messageTypes.sms && !messageTypes.whatsapp) {
      toast.warning('Please select at least one sending method (SMS or WhatsApp)');
      return;
    }

    if (messageTypes.sms && containsEmoji(reminderText)) {
      toast.error('SMS haitumi emoji. Tafadhali ondoa emoji au tumia Whatsapp pekee.');
      return;
    }

    if (selectedGuests.length === 0) {
      toast.warning('Please select at least one guest');
      return;
    }

    setSendingMessage(true);

    try {
      const payload = {
        event_id: currentEvent?.id,
        guest_ids: selectedGuests,
        pledge_ids: selectedGuests,
        message: reminderText,
        send_sms: messageTypes.sms,
        send_whatsapp: messageTypes.whatsapp
      };

      const response = await remindPledgeApi.sendReminder(payload);
      handleApiResponse(response);

      onHide();
      setSelectedGuests([]);
    } catch (err) {
      console.error('Error sending reminder:', err);
      if (err.response?.data) {
        handleApiResponse(err.response.data);
      } else if (err.data) {
        handleApiResponse(err.data);
      } else {
        showApiMessage({ success: false, status: 400, message: err.message || 'Failed to send messages' });
      }
    } finally {
      setSendingMessage(false);
    }
  };

  if (!isAuthenticated) return null;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered contentClassName="border-0 shadow-lg">
      <Modal.Body className="bg-white p-4 p-md-5 position-relative rounded">
        {/* Close Button */}
        <button type="button" className="btn-close position-absolute top-0 end-0 m-3" onClick={onHide} aria-label="Close"></button>

        {/* Header */}
        <div className="text-center mb-4">
          <h5 className="fw-normal" style={{ color: '#7a8bb0' }}>Kumbusha Michango</h5>
          <div className={reminderText.length > 160 ? "text-danger" : "text-muted"} style={{ fontSize: '0.85rem' }}>
            {reminderText.length} Characters {Math.max(1, Math.ceil(reminderText.length / 160))} Credits per message
          </div>
          {messageTypes.sms && containsEmoji(reminderText) && (
            <div className="text-danger mt-1 fw-bold" style={{ fontSize: '0.85rem' }}>
              SMS haitumi emoji. Tafadhali ondoa emoji au tumia Whatsapp pekee.
            </div>
          )}
          <div className="d-flex justify-content-center gap-4 mt-3">
            <Form.Check
              type="checkbox"
              id="sms-check-michango"
              label={<span className="text-secondary" style={{ fontSize: '0.9rem' }}><i className="bi bi-envelope me-1"></i> SMS ({credits.sms})</span>}
              checked={messageTypes.sms}
              onChange={e => setMessageTypes({ ...messageTypes, sms: e.target.checked })}
            />
            <Form.Check
              type="checkbox"
              id="wa-check-michango"
              label={<span className="text-secondary" style={{ fontSize: '0.9rem' }}><FaWhatsapp className="me-1" /> Whatsapp ({credits.whatsapp})</span>}
              checked={messageTypes.whatsapp}
              onChange={e => setMessageTypes({ ...messageTypes, whatsapp: e.target.checked })}
            />
          </div>
        </div>

        {/* Textarea */}
        <Form.Control
          as="textarea"
          rows={5}
          value={reminderText}
          onChange={(e) => setReminderText(e.target.value)}
          className="mb-2 shadow-sm"
          placeholder="Habari #jina, tunakukumbusha ahadi yako ya shughuli yetu..."
          style={{ resize: 'none', borderColor: '#c1cbe0', color: '#495057', borderRadius: '8px', fontSize: '0.9rem' }}
        />



        {/* Table Section */}
        <div className="mb-3">
          <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
            <div className="d-flex align-items-center gap-2">
              <h6 className="fw-semibold mb-0" style={{ color: '#475569' }}>Waliotoa Ahadi</h6>
              {selectedGuests.length > 0 && (
                <span className="badge bg-primary rounded-pill px-2 py-1" style={{ fontSize: '0.75rem' }}>
                  {selectedGuests.length} Selected
                </span>
              )}
            </div>

            <label className="d-flex align-items-center gap-1 text-secondary cursor-pointer mb-0" style={{ fontSize: '0.82rem', fontWeight: '500' }}>
              <input
                type="checkbox"
                id="select-all-michango"
                className="form-check-input"
                checked={filteredGuests.length > 0 && selectedGuests.length === filteredGuests.length}
                onChange={selectAllFiltered}
              />
              Select All
            </label>
          </div>

          <div className="d-flex flex-column flex-sm-row gap-2 align-items-stretch justify-content-between">
            <div style={{ flex: 1, minWidth: '180px' }}>
              <Select
                isMulti
                options={groupOptions}
                value={selectedGroups}
                onChange={setSelectedGroups}
                placeholder="Vikundi Vyote"
                className="basic-multi-select"
                classNamePrefix="select"
                styles={{
                  control: (base, state) => ({
                    ...base,
                    borderRadius: '20px',
                    borderColor: state.isFocused ? '#86b7fe' : '#dee2e8',
                    fontSize: '0.82rem',
                    minHeight: '38px',
                    height: '38px',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                    backgroundColor: '#ffffff',
                    paddingLeft: '4px',
                    cursor: 'pointer',
                    '&:hover': {
                      borderColor: '#cbd5e1'
                    }
                  }),
                  valueContainer: (base) => ({
                    ...base,
                    padding: '0 8px',
                    height: '36px',
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'nowrap',
                    overflow: 'hidden'
                  }),
                  indicatorsContainer: (base) => ({
                    ...base,
                    height: '36px'
                  }),
                  dropdownIndicator: (base) => ({
                    ...base,
                    padding: '4px'
                  }),
                  clearIndicator: (base) => ({
                    ...base,
                    padding: '4px'
                  }),
                  placeholder: (base) => ({
                    ...base,
                    color: '#6c757d'
                  }),
                  multiValue: (base) => ({
                    ...base,
                    borderRadius: '12px',
                    backgroundColor: '#e2e8f0',
                    fontSize: '0.75rem'
                  }),
                  menu: (base) => ({
                    ...base,
                    zIndex: 9999,
                    borderRadius: '12px',
                    fontSize: '0.82rem',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  })
                }}
              />
            </div>

            <div style={{ flex: 1, minWidth: '180px', maxWidth: '100%' }}>
              <Form.Control
                type="text"
                placeholder="Search guest..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="shadow-sm border"
                style={{
                  borderRadius: '20px',
                  padding: '0.375rem 1rem',
                  fontSize: '0.82rem',
                  height: '38px',
                  borderColor: '#dee2e8',
                  backgroundColor: '#ffffff'
                }}
              />
            </div>
          </div>
        </div>

        <div className="border-0">
          <StyledDataTable
            columns={columns}
            data={filteredGuests}
            progressPending={guestsLoading}
            progressComponent={<SpinnerLoader />}
            pagination={false}
            maxHeight="350px"
            noHeader
            responsive={false}
            onRowClicked={(row) => toggleContactSelection(row.id)}
            pointerOnHover
          />
        </div>

        {/* Floating Send Button */}
        <div className="mt-4 d-flex justify-content-start">
          <Button
            variant="primary"
            className="px-4 py-2 shadow"
            style={{ borderRadius: '8px', fontWeight: '500' }}
            onClick={handleSendReminder}
            disabled={sendingMessage || selectedGuests.length === 0 || !reminderText.trim() || (messageTypes.sms && containsEmoji(reminderText))}
          >
            {sendingMessage ? 'Sending...' : 'Send'}
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default KumbushaMichangoModal;
